import { motion } from "framer-motion";
import { Hammer, Gauge, CalendarCheck, Award } from "lucide-react";
import { useTexts, useExtras } from "@/lib/texts";

const EASE = [0.16, 1, 0.3, 1];
const PILLAR_ICONS = [Hammer, Gauge, CalendarCheck, Award];

export const About = () => {
    const texts = useTexts();
    const { pillars, images } = useExtras();

    return (
    <section
        id="chi-siamo"
        data-testid="about-section"
        className="bg-[#F0EDE6] py-20 sm:py-28 lg:py-36"
    >
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-16">
            <div className="relative lg:col-span-5">
                <motion.div
                    initial={{ clipPath: "inset(0 100% 0 0)" }}
                    whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1.2, ease: EASE }}
                    className="relative aspect-[4/5] overflow-hidden"
                >
                    <img
                        src={images.about_main}
                        alt="Maestro scalpellino al lavoro sulla pietra"
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                        data-testid="about-artisan-image"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/50 via-transparent to-transparent" />
                    <div className="absolute bottom-5 left-5">
                        <span className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-[#E75C15]">
                            {texts.about_caption}
                        </span>
                        <p className="font-display text-xl italic text-stone-100">
                            {texts.about_quote}
                        </p>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                    className="absolute -bottom-10 -right-2 hidden w-48 border-4 border-[#F0EDE6] shadow-xl sm:block lg:-right-10 lg:w-56"
                >
                    <img
                        src={images.about_secondary}
                        alt="Direttore di cantiere esamina i disegni esecutivi"
                        loading="lazy"
                        decoding="async"
                        className="aspect-square w-full object-cover opacity-75 transition-opacity duration-500 hover:opacity-100"
                        data-testid="about-blueprint-image"
                    />
                </motion.div>

                <motion.span
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
                    className="absolute -top-6 -left-4 flex h-24 w-24 rotate-[-8deg] items-center justify-center whitespace-pre-line rounded-full border border-[#E75C15] bg-[#1C1917] text-center font-display text-sm font-semibold leading-tight text-[#E75C15] sm:-left-6"
                    data-testid="about-badge"
                >
                    {texts.about_badge}
                </motion.span>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
                <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: EASE }}
                    className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]"
                >
                    {texts.about_eyebrow}
                </motion.span>

                <motion.h2
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                    className="mt-4 font-display text-3xl font-light tracking-tight text-stone-900 sm:text-4xl lg:text-5xl"
                >
                    {texts.about_heading}
                </motion.h2>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
                    className="mt-6 space-y-4 text-base leading-relaxed text-stone-600"
                >
                    <p>{texts.about_p1}</p>
                    <p>{texts.about_p2}</p>
                </motion.div>

                <div className="mt-10 space-y-6">
                    {pillars.map((p, i) => {
                        const Icon = PILLAR_ICONS[i % PILLAR_ICONS.length];
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: 24 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: EASE }}
                                className="flex items-start gap-4 border-b border-stone-900/10 pb-6"
                                data-testid={`about-pillar-${i}`}
                            >
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-[#E75C15]/50 text-[#C24E0F]">
                                    <Icon className="h-5 w-5" />
                                </span>
                                <div>
                                    <h3 className="font-display text-xl font-medium text-stone-900">
                                        {p.title}
                                    </h3>
                                    <p className="mt-1 text-sm leading-relaxed text-stone-600">{p.desc}</p>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    </section>
    );
};
